import type { TFunction } from 'i18next'

export type KnownMosquitoDisplayCategory = 'culex' | 'aedes' | 'otherMosquito'
export type MosquitoDisplayCategory =
    | KnownMosquitoDisplayCategory
    | (string & {})
export interface MosquitoDisplayFilterOption {
    value: string
    label: string
}
export interface ConfiguredPestTypeOption {
    value: string
    label: string
}

export const HISTORY_DETECTION_CLASS_DISPLAY_PREFIX =
    'history:detail.detectionClasses'

const CONFIGURED_CULEX_KEYS = new Set(['culex', '库蚊', '庫蚊'])
const CONFIGURED_AEDES_KEYS = new Set(['aedes', '伊蚊'])
const CONFIGURED_OTHER_MOSQUITO_KEYS = new Set([
    'othermosquito',
    'otherMosquito',
    '其他蚊虫',
    '其它蚊虫',
    '其他蚊蟲',
    '其它蚊蟲',
])
export const MOSQUITO_DISPLAY_ORDER: KnownMosquitoDisplayCategory[] = [
    'culex',
    'aedes',
    'otherMosquito',
]

const isKnownMosquitoDisplayCategory = (
    category: MosquitoDisplayCategory
): category is KnownMosquitoDisplayCategory =>
    MOSQUITO_DISPLAY_ORDER.includes(category as KnownMosquitoDisplayCategory)

export const normalizeConfiguredDisplayCategory = (
    value: string | null | undefined
): MosquitoDisplayCategory | null => {
    const normalized = value?.trim()
    if (!normalized) {
        return null
    }

    const lower = normalized.toLowerCase()
    if (CONFIGURED_CULEX_KEYS.has(lower) || CONFIGURED_CULEX_KEYS.has(normalized)) {
        return 'culex'
    }
    if (CONFIGURED_AEDES_KEYS.has(lower) || CONFIGURED_AEDES_KEYS.has(normalized)) {
        return 'aedes'
    }
    if (
        CONFIGURED_OTHER_MOSQUITO_KEYS.has(lower) ||
        CONFIGURED_OTHER_MOSQUITO_KEYS.has(normalized)
    ) {
        return 'otherMosquito'
    }
    return normalized
}

export const getPredictionDisplayCategory = (
    prediction:
        | {
              eng_class?: unknown
              class?: unknown
              pest?: unknown
              label?: unknown
              display_category_key?: unknown
              display_category_name?: unknown
          }
        | null
        | undefined
): MosquitoDisplayCategory | null => {
    if (!prediction) {
        return null
    }

    const displayCategoryName = prediction.display_category_name
    if (
        typeof displayCategoryName === 'string' &&
        displayCategoryName.trim()
    ) {
        return displayCategoryName.trim()
    }

    const displayCategoryKey = prediction.display_category_key
    if (typeof displayCategoryKey === 'string' && displayCategoryKey.trim()) {
        return normalizeConfiguredDisplayCategory(displayCategoryKey)
    }

    return null
}

export const getMosquitoCategoryDisplayName = (
    category: MosquitoDisplayCategory,
    t: TFunction,
    namespacePrefix: string
): string =>
    isKnownMosquitoDisplayCategory(category)
        ? t(`${namespacePrefix}.${category}`)
        : category

export const sortMosquitoDisplayCategories = (
    categories: Iterable<MosquitoDisplayCategory>
): MosquitoDisplayCategory[] => {
    const available = new Set<MosquitoDisplayCategory>(categories)
    const ordered: MosquitoDisplayCategory[] = MOSQUITO_DISPLAY_ORDER.filter((category) =>
        available.has(category)
    )
    ordered.push(
        ...Array.from(available)
            .filter(
                (category) =>
                    !isKnownMosquitoDisplayCategory(category)
            )
            .sort((left, right) => left.localeCompare(right))
    )
    return ordered
}

export const getMosquitoFilterValue = (
    category: MosquitoDisplayCategory
): string => category

export const buildMosquitoDisplayFilterOptions = (
    rawValues: Iterable<string | null | undefined>,
    t: TFunction,
    namespacePrefix: string
): MosquitoDisplayFilterOption[] => {
    const categories = new Set<MosquitoDisplayCategory>()
    for (const rawValue of rawValues) {
        const category = normalizeConfiguredDisplayCategory(rawValue)
        if (category) {
            categories.add(category)
        }
    }

    return sortMosquitoDisplayCategories(categories).map((category) => ({
        value: getMosquitoFilterValue(category),
        label: getMosquitoCategoryDisplayName(category, t, namespacePrefix),
    }))
}

export const buildConfiguredPestTypeFilterOptions = (
    rawOptions: Iterable<
        string | ConfiguredPestTypeOption | null | undefined
    >
): MosquitoDisplayFilterOption[] => {
    const options: MosquitoDisplayFilterOption[] = []
    const seenValues = new Set<string>()

    for (const rawOption of rawOptions) {
        const value =
            typeof rawOption === 'string'
                ? rawOption.trim()
                : rawOption?.value?.trim()
        const label =
            typeof rawOption === 'string'
                ? rawOption.trim()
                : rawOption?.label?.trim() || value

        if (!value || seenValues.has(value)) {
            continue
        }

        seenValues.add(value)
        options.push({
            value,
            label: label || value,
        })
    }

    return options
}

export const mergeMosquitoDisplayCounts = (
    rawCounts: Record<string, number> | null | undefined
): Record<string, number> => {
    const counts: Record<string, number> = {}
    for (const [rawType, rawCount] of Object.entries(rawCounts ?? {})) {
        const category = normalizeConfiguredDisplayCategory(rawType)
        if (!category || rawCount <= 0) {
            continue
        }

        const value = getMosquitoFilterValue(category)
        counts[value] = (counts[value] ?? 0) + rawCount
    }
    return counts
}
